import type { AxiosError } from 'axios'
import axios from 'axios'

import { logoutUser } from './storage-service/storageService'

export interface ErrorResponse {
    entityName?: string
    errorKey?: string
    message: string
    path?: string
    detail?: string
    params?: any
    status: string
    title: string
    type: string
}

export const errorHandler = async (err: AxiosError<ErrorResponse>): Promise<any> => {
    const isAxiosErr = axios.isAxiosError(err)

    if (!isAxiosErr) return Promise.reject(err)

    const { status: axiosStatus, response } = err

    const status = axiosStatus ?? response?.status

    try {
        if (status === 500) return Promise.reject('Unable to complete request! Server unavailable')

        // If service is unavailable (maintenance mode) then reload page.
        if (status === 503) window.location.reload()

        if (err.code === 'ERR_NETWORK') {
            window.location.reload()
            return
        }

        // Only a 401 means the session is gone. A 502/504 is a gateway failure, and
        // ending the session on those drops a customer mid-checkout: the token is
        // cleared from shared storage, and every later authenticated call fails.
        if (status === 401 || status === 502 || status === 504) {
            if (status === 401) logoutUser()
            return Promise.reject(`error.http.${status}`)
        }

        if (response) return Promise.reject(response.data)

        return Promise.reject(err)
    } catch (e) {
        return Promise.reject('Unable to complete request')
    }
}

export interface StandardErrorResponse {
    message: string
    params?: any
    response?: any
    status?: number
    originalError?: any
}

/**
 * Type guard to check if an error is a StandardErrorResponse
 * @param error Any value to check
 * @returns true if the error is a StandardErrorResponse
 */
export const isStandardErrorResponse = (error: any): error is StandardErrorResponse => {
    return (
        error &&
        typeof error === 'object' &&
        typeof error.message === 'string' &&
        // The following properties might be undefined, but they should have the correct types if they exist
        (error.params === undefined || typeof error.params === 'object') &&
        (error.response === undefined || typeof error.response === 'object') &&
        (error.status === undefined || typeof error.status === 'number') &&
        'originalError' in error
    )
}

const UNAUTHORIZED_STATUS = 401

/**
 * A checkout session that ended mid-flow surfaces as a bare 401: Spring's entry point
 * answers `sendError(401)`, which carries no `x-leanpaycoreapp-error` header, so there
 * is no Leanpay error code to match on. Callers need to tell that apart from an ordinary
 * rejected request, because the only way forward is to authenticate again.
 */
export const isSessionExpiredError = (error: unknown): boolean =>
    // Boolean(): isStandardErrorResponse short-circuits on its `error &&` operand, so a
    // nullish input would otherwise leak out of a function declared to return a boolean.
    Boolean(isStandardErrorResponse(error) && error.status === UNAUTHORIZED_STATUS)

export const preQualifiedErrorHandler = async (err: AxiosError<any> | Error): Promise<any> => {
    const isAxiosErr = axios.isAxiosError(err)

    if (!isAxiosErr) {
        const error: StandardErrorResponse = {
            message: err instanceof Error ? String(err.message) : 'Unknown error',
            originalError: err,
        }

        if (window.location.hostname === 'localhost') {
            console.log('👺 Axios onReject Interceptor Log:', JSON.stringify(err))
        }
        return Promise.reject(error)
    }

    const { response } = err

    // If service is unavailable (maintenance mode) then reload page.
    if (response?.status === 503) {
        window.location.reload()
    }

    // Only a 401 means the session is gone — see the note in errorHandler above.
    if (response?.status === 401 || response?.status === 502 || response?.status === 504) {
        if (response.status === 401) logoutUser()
        if (window.location.hostname === 'localhost') {
            console.log(
                '👺 Axios onReject Interceptor Log:',
                JSON.stringify({
                    message: String(response.status),
                    status: response.status,
                    response: response,
                    originalError: err,
                }),
            )
        }
        return Promise.reject({
            message: String(response.status),
            status: response.status,
            response: response,
            originalError: err,
        } as StandardErrorResponse)
    }
    // Get core app error code or default error message.
    const message =
        response?.headers['x-leanpaycoreapp-error'] ??
        response?.headers['x-leanpaycoreapp-defaultmessage']

    // Get core app error params.
    const paramsStr = response?.headers['x-leanpaycoreapp-params']
    const params = paramsStr ? JSON.parse(paramsStr) : undefined

    // Construct standardized error response
    const errorResponse: StandardErrorResponse = {
        message: String(
            message ?? response?.status ?? (err instanceof Error ? err.message : '') ?? '',
        ),
        params,
        status: response?.status,
        response: response,
        originalError: err,
    }

    if (window.location.hostname === 'localhost') {
        console.log('👺 Axios onReject Interceptor Log:', JSON.stringify(errorResponse))
    }
    return Promise.reject(errorResponse)
}

export const vendorErrorHandler = async (err: AxiosError<any>): Promise<any> => {
    const isAxiosErr = axios.isAxiosError(err)

    if (!isAxiosErr) return Promise.reject(err)

    const { response } = err

    // If service is unavailable (maintenance mode) then reload page.
    if (response?.status === 503) {
        window.location.reload()
    }

    // Only a 401 means the session is gone — see the note in errorHandler above.
    if (response?.status === 401 || response?.status === 502 || response?.status === 504) {
        if (response.status === 401) logoutUser()
        return Promise.reject(response.status)
    }
    // Get core app error code or default error message.
    const message =
        response?.headers['x-leanpaycoreapp-error'] ??
        response?.headers['x-leanpaycoreapp-defaultmessage']

    // Get core app error params.
    const params = response?.headers['x-leanpaycoreapp-params']

    return Promise.reject(String(message ?? response?.status))
}
